import type {
  Badges,
  CarrierFilterItem,
  CarrierWithPrice,
  FilterOptions,
  LocationByLeg,
  LocationWithPrice,
  OfferCarbonEmissions,
  SearchResult,
  SearchResults,
  SortKey,
  SortOption,
  SortOptions,
  WithPrice,
} from '@optionway/models';
import { modelsImpl } from '@optionway/models';
import { toCacheKey } from '@pinia/colada';
import { DateTime, Duration } from 'luxon';

interface SearchResultIndexStore {
  allSearchResults: SearchResult[];
  allFilterOptions: FilterOptions;
  allSortOptions: SortOptions;
  allAsyncCarbonOfferUuids: Set<string>;
}

interface SortTab {
  key: SortKey;
  referenceValue?: SortOption;
}

export const useSearchResultIndexStore = defineStore('search-result-index', () => {
  const { getEntries } = useQueryCache();
  const criteria = useSearchCriteriaStore();
  const sortOptionsStore = useSortOptionsStore();

  // state
  const previousCount = ref(5);
  // getter
  const cachedSearchData = computed<SearchResultIndexStore>(() => {
    const resultsMap = new Map<string, SearchResult>();
    const carriersMap = new Map<string, CarrierWithPrice>();
    const locationsPerLeg: Map<string, LocationWithPrice>[] = [];
    const cheapestOptions: SortOption[] = [];
    const fastestOptions: SortOption[] = [];
    const bestOfferOptions: SortOption[] = [];
    const carbonEmissionOptions: SortOption[] = [];
    const previousKey = criteria.searchedCriteriaKey;
    // Async CO₂ (offerUuid -> tonnes | undefined) + carbon sort candidates, read from ALL carbon-emission
    // cache entries: offerUuids are globally unique so CO₂ is reused across searches/filters (immutable per offer).
    // Keys = every offer already answered (value may be undefined) → drives offerUuidsMissingCarbon.
    const asyncCarbonByOffer = new Map<string, number | undefined>();
    // Loop-dedicated candidates: collected per cache entry, then compacted (undefined dropped) into the cache consts above.
    const cheapestCandidates: (SortOption | undefined)[] = [];
    const fastestCandidates: (SortOption | undefined)[] = [];
    const bestOfferCandidates: (SortOption | undefined)[] = [];
    const carbonEmissionCandidates: (SortOption | undefined)[] = [];
    for (const entry of getEntries({ key: ['carbon-emission'], exact: false })) {
      const data = entry.state.value.data;
      if (!isOfferCarbonEmissions(data)) continue;
      collectAsyncCarbon(data, asyncCarbonByOffer);
    }
    for (const entry of previousKey
      ? getEntries({ key: ['search-results', toCacheKey([previousKey])], exact: false })
      : []) {
      const data = entry.state.value.data;
      if (!isSearchResults(data)) continue;
      mergeSearchResults(resultsMap, data.searchResult, asyncCarbonByOffer);
      mergeByIataCode(carriersMap, data.filterOptions?.carriers);
      mergeLocationsByLeg(locationsPerLeg, data.filterOptions?.byLeg);
      cheapestCandidates.push(data.sortOptions?.cheapest);
      fastestCandidates.push(data.sortOptions?.fastest);
      bestOfferCandidates.push(data.sortOptions?.bestOffer);
      const carbonEmission = data.sortOptions?.carbonEmission;
      carbonEmissionCandidates.push(carbonEmission?.carbonEmission ? carbonEmission : undefined);
    }
    carbonEmissionCandidates.push(bestAsyncCarbonCandidate(asyncCarbonByOffer, resultsMap));
    // Fill the cache consts with defined sort options only (compact drops the undefined candidates).
    cheapestOptions.push(...useCompact(cheapestCandidates));
    fastestOptions.push(...useCompact(fastestCandidates));
    bestOfferOptions.push(...useCompact(bestOfferCandidates));
    carbonEmissionOptions.push(...useCompact(carbonEmissionCandidates));
    const allFilterOptions: FilterOptions = {
      carriers: [...carriersMap.values()],
      byLeg: locationsPerLeg.map((map) => ({ locations: [...map.values()] })),
    };
    const allSortOptions: SortOptions = {
      cheapest: toSortOption(sortByPrice(cheapestOptions.map((sortOption) => toFakeSearchResult(sortOption)))[0]),
      fastest: toSortOption(sortByDuration(fastestOptions.map((sortOption) => toFakeSearchResult(sortOption)))[0]),
      bestOffer: toSortOption(sortByRelevance(bestOfferOptions.map((sortOption) => toFakeSearchResult(sortOption)))[0]),
      carbonEmission: toSortOption(
        sortByCarbonEmission(carbonEmissionOptions.map((sortOption) => toFakeSearchResult(sortOption)))[0],
      ),
    };
    return {
      allSearchResults: [...resultsMap.values()],
      allFilterOptions,
      allSortOptions,
      allAsyncCarbonOfferUuids: new Set(asyncCarbonByOffer.keys()),
    };
  });

  // getter — offers with no CO₂ yet AND not already requested this search (deterministic, sorted → stable cache key)
  const offerUuidsMissingCarbon = computed<string[]>(() => {
    const { allSearchResults, allAsyncCarbonOfferUuids } = cachedSearchData.value;
    return allSearchResults
      .filter((result) => !result.carbonEmission && !allAsyncCarbonOfferUuids.has(result.offerUuid))
      .map((result) => result.offerUuid)
      .toSorted((a, b) => a.localeCompare(b));
  });

  // getter — true when at least one offer has a CO₂ value (sync or async merged); model-derived data.
  const hasCarbonEmission = computed<boolean>(() =>
    cachedSearchData.value.allSearchResults.some((result) => result.carbonEmission),
  );

  // getter
  const allFilterOptions = computed<FilterOptions>(() => cachedSearchData.value.allFilterOptions);

  // getter
  const skeletonResults = computed<SearchResult[]>(() => {
    const currentTripType = criteria.searchRequest.tripType;
    const skeletonLegs = criteria.searchRequest.legs
      .filter(
        (leg, index) =>
          leg.departureDate.trim() &&
          ((currentTripType === 'ONE_WAY' && index === 0) ||
            (currentTripType === 'ROUND_TRIP' && index <= 1) ||
            currentTripType === 'MULTI_DEST'),
      )
      .map(() => new modelsImpl.LegImpl());
    return Array.from({ length: previousCount.value }, (_, index) => {
      const result = new modelsImpl.SearchResultImpl();
      result.offerUuid = `skeleton-${index}`;
      // A fabricated row still honours the SearchResult contract: the card spreads it as props
      // (v-bind), and Vue flags every skeleton once required fields are left undefined.
      result.updatedAt = '';
      result.changeType = 'NO_CHANGE';
      result.legs = skeletonLegs;
      return result;
    });
  });

  // getter — the six result filters, in the order the panels stack them
  const filteredSearchResults = computed<SearchResult[]>(() => {
    const stops = useStopsFilterStore();
    const hours = useHoursFilterStore();
    const duration = useDurationFilterStore();
    const airports = useAirportsFilterStore();
    const airlines = useAirlinesFilterStore();

    let results = cachedSearchData.value.allSearchResults;

    results = filterByMaxStops(results, stops.maxStop);
    results = filterByAirportChange(results, stops.disableMultiAirportConnection);
    results = filterByHours(results, hours.committedLegHours);
    results = filterByDuration(results, duration.legs);
    results = filterByAirports(
      results,
      airports.selectedDeparture,
      airports.selectedArrival,
      airports.allDepartureSelected,
      airports.allArrivalSelected,
    );
    // The filter store's rows carry the backend price only; the displayed price lives in filteredCarriers,
    // which reads this getter — no cycle.
    results = filterByAirlines(results, airlines.airlines);

    results = filterByConnectionTime(results, stops.minConnectionTime, stops.maxConnectionTime);

    return results;
  });

  // getter — internal: lowest averagePerPax among the DISPLAYED results per carrier, feeding the panel rows below
  const carrierPriceByCode = computed<Map<string, number>>(() => minPriceByCarrier(filteredSearchResults.value));
  // getter — internal: the same per airport
  const locationPriceByCode = computed<Map<string, number>>(() => minPriceByLocation(filteredSearchResults.value));

  // getter — the airline panel's rows: the filter store's selection, priced from the displayed results.
  // The backend price stays untouched on useAirlinesFilterStore().airlines; this is the derived list.
  const filteredCarriers = computed<CarrierFilterItem[]>(() =>
    useAirlinesFilterStore().airlines.map((carrier) => withDisplayedPrice(carrier, carrierPriceByCode.value)),
  );

  // getter — the two airport panels (leg-0 departure and arrival), same rule
  const filteredDepartureAirports = computed<LocationWithPrice[]>(() =>
    useAirportsFilterStore().departureAirports.map((airport) => withDisplayedPrice(airport, locationPriceByCode.value)),
  );
  // getter — the arrival panel, same rule
  const filteredArrivalAirports = computed<LocationWithPrice[]>(() =>
    useAirportsFilterStore().arrivalAirports.map((airport) => withDisplayedPrice(airport, locationPriceByCode.value)),
  );

  // getter
  const allSortOptions = computed<SortOptions>(() => cachedSearchData.value.allSortOptions);

  // getter — true when filters are actually reducing the full result set (passed to snapshotFilter)
  const filtersEffective = computed(
    () =>
      cachedSearchData.value.allSearchResults.length > 0 &&
      filteredSearchResults.value.length !== cachedSearchData.value.allSearchResults.length,
  );

  // getter
  const filteredSortOptions = computed<SortOptions>(() => {
    const results = filteredSearchResults.value;
    const { allSearchResults, allSortOptions } = cachedSearchData.value;

    if (results.length === allSearchResults.length) {
      return allSortOptions;
    }

    const filtered: SortOptions = {
      cheapest: toSortOption(sortByPrice(results)[0]),
      fastest: toSortOption(sortByDuration(results)[0]),
      bestOffer: toSortOption(sortByRelevance(results)[0]),
      carbonEmission: toSortOption(sortByCarbonEmission(results)[0]),
    };

    return filtered;
  });

  // getter
  const sortOptions = computed<SortTab[]>(() => {
    const { cheapest, fastest, bestOffer, carbonEmission } = filteredSortOptions.value;
    return [
      { key: 'price', referenceValue: cheapest },
      { key: 'duration', referenceValue: fastest },
      { key: 'bestOffer', referenceValue: bestOffer },
      { key: 'carbonEmission', referenceValue: carbonEmission },
    ];
  });

  // getter
  const availableSearchResults = computed<SearchResult[]>(() => {
    const results = filteredSearchResults.value;
    const reference = previousFilteredSortOptions.value ?? allSortOptions.value;

    let sorted: SearchResult[];
    switch (sortOptionsStore.sortBy) {
      case 'duration': {
        sorted = sortByDuration(results);
        break;
      }
      case 'carbonEmission': {
        sorted = sortByCarbonEmission(results);
        break;
      }
      case 'price': {
        sorted = sortByPrice(results);
        break;
      }
      default: {
        sorted = sortByRelevance(results);
      }
    }

    const fastestMinutes = durationToMinutes(reference.fastest?.travelDuration);
    return sorted.map((result) => ({ ...result, badges: resultBadges(result, reference, fastestMinutes) }));
  });

  // getter
  const noResult = computed(() => availableSearchResults.value.length === 0);

  // state — filtered results when the filtering search was armed
  const previousFilteredCount = ref(0);

  // getter — the filtering search added filtered results; filtered, not merged: the search ignores arrival hours
  const hasNewFilteredResults = computed(() => filteredSearchResults.value.length > previousFilteredCount.value);

  // state — undefined until first snapshotSortOptions call; reset to undefined on new search
  const previousFilteredSortOptions = ref<SortOptions | undefined>(undefined);

  // action — pure evaluation: reads OLD previousFilteredSortOptions before snapshotSortOptions overwrites it
  const hasSortOptionsChanged = (): boolean => {
    if (!filtersEffective.value) {
      console.log('Filtered sort options match previous snapshot — deactivating server-side filter query');
      return false;
    }
    const reference = previousFilteredSortOptions.value ?? allSortOptions.value;
    const hasChanged = sortOptionsHaveChanged(filteredSortOptions.value, reference);
    console.log(
      hasChanged
        ? 'Filtered sort options changed vs previous snapshot — activating server-side filter query'
        : 'Filtered sort options match previous snapshot — deactivating server-side filter query',
    );
    return hasChanged;
  };

  // action
  const snapshotSortOptions = () => {
    previousFilteredSortOptions.value = filteredSortOptions.value;
  };

  // action
  const resetSortOptions = () => {
    previousFilteredSortOptions.value = undefined;
  };

  // action
  const snapshotFilteredCount = () => {
    previousFilteredCount.value = filteredSearchResults.value.length;
  };

  // action
  const snapshotCount = () => {
    previousCount.value = filteredSearchResults.value.length || 5;
  };

  return {
    allFilterOptions,
    filteredCarriers,
    filteredDepartureAirports,
    filteredArrivalAirports,
    availableSearchResults,
    hasNewFilteredResults,
    hasCarbonEmission,
    noResult,
    offerUuidsMissingCarbon,
    skeletonResults,
    snapshotCount,
    snapshotFilteredCount,
    snapshotSortOptions,
    sortOptions,
    hasSortOptionsChanged,
    resetSortOptions,
  };
});

function isSearchResults(data: unknown): data is SearchResults {
  return typeof data === 'object' && data !== null && 'searchResult' in data;
}

function isOfferCarbonEmissions(data: unknown): data is OfferCarbonEmissions {
  return typeof data === 'object' && data !== null && 'offerCarbonEmission' in data;
}

function collectAsyncCarbon(data: OfferCarbonEmissions, target: Map<string, number | undefined>) {
  for (const { offerUuid, carbonEmission } of data.offerCarbonEmission) {
    target.set(offerUuid, carbonEmission); // record every answer (incl. undefined/0) — the "answered" set
  }
}

// Lowest-CO₂ async offer as a single sort candidate, taken FULL from the merged map — the async response has only
// offerUuid + CO₂, so the winner needs the merged offer's price/duration.
function bestAsyncCarbonCandidate(
  asyncCarbonByOffer: Map<string, number | undefined>,
  resultsMap: Map<string, SearchResult>,
): SortOption | undefined {
  const asyncOffers = [...asyncCarbonByOffer.keys()]
    .map((offerUuid) => resultsMap.get(offerUuid))
    .filter((offer): offer is SearchResult => !!offer?.carbonEmission);
  return toSortOption(sortByCarbonEmission(asyncOffers)[0]);
}

function mergeSearchResults(
  map: Map<string, SearchResult>,
  results: SearchResult[],
  asyncCarbonByOffer: Map<string, number | undefined>,
) {
  for (const result of results) {
    const existing = map.get(result.offerUuid);
    if (!existing || result.updatedAt > existing.updatedAt) {
      // Async CO₂ is the source of truth and overrides the sync value; falls back to sync when absent.
      map.set(result.offerUuid, {
        ...result,
        carbonEmission: asyncCarbonByOffer.get(result.offerUuid) ?? result.carbonEmission,
      });
    }
  }
}

function mergeLocationsByLeg(legMaps: Map<string, LocationWithPrice>[], byLeg?: LocationByLeg[]) {
  for (const [index, leg] of (byLeg ?? []).entries()) {
    if (!legMaps[index]) legMaps[index] = new Map();
    mergeByIataCode(legMaps[index], leg.locations);
  }
}

function mergeByIataCode<T extends { iataCode?: string }>(map: Map<string, T>, items?: T[]) {
  for (const item of items ?? []) {
    if (item.iataCode && !map.has(item.iataCode)) {
      map.set(item.iataCode, item);
    }
  }
}

function filterByMaxStops(items: SearchResult[], maxStop?: number): SearchResult[] {
  if (maxStop === undefined) {
    return items;
  }
  return items.filter((result) => result.legs.every((leg) => (leg.stopover?.count ?? 0) <= maxStop));
}

function filterByConnectionTime(items: SearchResult[], minHours?: number, maxHours?: number): SearchResult[] {
  if (minHours === undefined && maxHours === undefined) {
    return items;
  }
  return items.filter((result) =>
    result.legs.every((leg) => {
      const details = leg.stopover?.details ?? [];
      return details.every((detail) => {
        const duration = detail.duration ? Duration.fromISO(detail.duration) : undefined;
        if (!duration?.isValid) {
          return true;
        }
        const hours = duration.as('hours');
        return (minHours === undefined || hours >= minHours) && (maxHours === undefined || hours <= maxHours);
      });
    }),
  );
}

function filterByAirportChange(items: SearchResult[], disable?: boolean): SearchResult[] {
  if (!disable) {
    return items;
  }
  return items.filter((result) => result.changeType !== 'AIRPORT');
}

function filterByHours(
  items: SearchResult[],
  filterLegs: { departureValue: { start: number; end: number }; arrivalValue: { start: number; end: number } }[],
): SearchResult[] {
  return items.filter((result) =>
    result.legs.every((leg, index) => {
      const filter = filterLegs[index];
      if (!filter) {
        return true;
      }
      const departureDateTime = leg.departureDateTime ? DateTime.fromISO(leg.departureDateTime) : undefined;
      if (departureDateTime?.isValid) {
        const time = departureDateTime.diff(departureDateTime.startOf('day')).as('hours');
        if (time < filter.departureValue.start || time > filter.departureValue.end) {
          return false;
        }
      }
      const arrivalDateTime = leg.arrivalDateTime ? DateTime.fromISO(leg.arrivalDateTime) : undefined;
      if (arrivalDateTime?.isValid) {
        const time = arrivalDateTime.diff(arrivalDateTime.startOf('day')).as('hours');
        if (time < filter.arrivalValue.start || time > filter.arrivalValue.end) {
          return false;
        }
      }
      return true;
    }),
  );
}

function filterByDuration(items: SearchResult[], filterLegs: { durationValue: number }[]): SearchResult[] {
  return items.filter((result) =>
    result.legs.every((leg, index) => {
      const filter = filterLegs[index];
      if (!filter || filter.durationValue >= 24) {
        return true;
      }
      const duration = leg.duration ? Duration.fromISO(leg.duration) : undefined;
      if (!duration?.isValid) {
        return true;
      }
      return duration.as('hours') <= filter.durationValue;
    }),
  );
}

function filterByAirlines(items: SearchResult[], carriers: CarrierFilterItem[]): SearchResult[] {
  if (carriers.every((carrier) => carrier.selected)) {
    return items;
  }
  const selectedSet = new Set(
    carriers.flatMap((carrier) => (carrier.selected && carrier.iataCode ? [carrier.iataCode] : [])),
  );
  return items.filter((result) => {
    const carrierCodes = result.legs.map((leg) => leg.carrier?.iataCode).filter(Boolean);
    if (carrierCodes.length === 0) {
      return true;
    }
    return carrierCodes.every((code) => code && selectedSet.has(code));
  });
}

function filterByAirports(
  items: SearchResult[],
  selectedDeparture: string[],
  selectedArrival: string[],
  allDepartureSelected: boolean,
  allArrivalSelected: boolean,
): SearchResult[] {
  if (allDepartureSelected && allArrivalSelected) {
    return items;
  }
  const departureSet = new Set(selectedDeparture);
  const arrivalSet = new Set(selectedArrival);
  return items.filter((result) => {
    const depCode = result.legs[0]?.departure?.iataCode;
    const arrivalCode = result.legs[0]?.arrival?.iataCode;
    if (!allDepartureSelected && depCode && !departureSet.has(depCode)) {
      return false;
    }
    if (!allArrivalSelected && arrivalCode && !arrivalSet.has(arrivalCode)) {
      return false;
    }
    return true;
  });
}

// A panel row carries its displayed price in the model's own `price` field — none when no displayed
// result carries the code.
function withDisplayedPrice<T extends WithPrice & { iataCode?: string }>(item: T, prices: Map<string, number>): T {
  const averagePerPax = prices.get(item.iataCode ?? '');
  return { ...item, price: averagePerPax === undefined ? undefined : { averagePerPax } };
}

function minPriceByCarrier(items: SearchResult[]): Map<string, number> {
  const prices = new Map<string, number>();

  for (const item of items) {
    const price = item.price?.averagePerPax;
    if (price === undefined) {
      continue;
    }

    for (const leg of item.legs) {
      const iataCode = leg.carrier?.iataCode;

      if (iataCode && price < (prices.get(iataCode) ?? Infinity)) {
        prices.set(iataCode, price);
      }
    }
  }

  return prices;
}

function minPriceByLocation(items: SearchResult[]): Map<string, number> {
  const prices = new Map<string, number>();

  for (const item of items) {
    const price = item.price?.averagePerPax;
    if (price === undefined) {
      continue;
    }

    const departureIataCode = item.legs[0]?.departure?.iataCode;
    if (departureIataCode && price < (prices.get(departureIataCode) ?? Infinity)) {
      prices.set(departureIataCode, price);
    }

    const arrivalIataCode = item.legs[0]?.arrival?.iataCode;
    if (arrivalIataCode && price < (prices.get(arrivalIataCode) ?? Infinity)) {
      prices.set(arrivalIataCode, price);
    }
  }

  return prices;
}

function sortByPrice(items: SearchResult[]): SearchResult[] {
  return items.toSorted((a, b) => (a.price?.total ?? 0) - (b.price?.total ?? 0));
}

function sortByRelevance(items: SearchResult[]): SearchResult[] {
  return items.toSorted((a, b) => (b.relevanceScore ?? -Infinity) - (a.relevanceScore ?? -Infinity));
}

function sortByDuration(items: SearchResult[]): SearchResult[] {
  return items.toSorted((a, b) => {
    const durationA = a.travelDuration ? Duration.fromISO(a.travelDuration).as('minutes') : Infinity;
    const durationB = b.travelDuration ? Duration.fromISO(b.travelDuration).as('minutes') : Infinity;
    return durationA - durationB;
  });
}

function sortByCarbonEmission(items: SearchResult[]): SearchResult[] {
  return items.toSorted((a, b) => {
    if (!a.carbonEmission && !b.carbonEmission) {
      return 0;
    }
    if (!a.carbonEmission) {
      return 1;
    }
    if (!b.carbonEmission) {
      return -1;
    }
    return a.carbonEmission - b.carbonEmission;
  });
}

function toFakeSearchResult(option: SortOption): SearchResult {
  const result = new modelsImpl.SearchResultImpl();
  result.price = option.price;
  result.travelDuration = option.travelDuration;
  result.relevanceScore = option.relevanceScore;
  result.carbonEmission = option.carbonEmission;
  return result;
}

function toSortOption(result?: SearchResult): SortOption | undefined {
  if (!result) return undefined;
  return {
    price: result.price,
    travelDuration: result.travelDuration,
    relevanceScore: result.relevanceScore,
    carbonEmission: result.carbonEmission,
  };
}

function sortOptionsHaveChanged(filtered: SortOptions, reference: SortOptions): boolean {
  return (
    // hasSortOptionWorsened(sortByDuration, filtered.fastest, reference.fastest) ||
    // hasSortOptionWorsened(sortByCarbonEmission, filtered.carbonEmission, reference.carbonEmission) ||
    isSortOptionWorse(sortByPrice, filtered.cheapest, reference.cheapest) ||
    isSortOptionWorse(sortByRelevance, filtered.bestOffer, reference.bestOffer)
  );
}

// Sorts [current, previous] — the best value lands at [0].
// If previous lands at [0], previous was better → current is worse → BFF warranted.
//
//   current=200€, previous=100€ → sortByPrice → [previous(100€), current(200€)]
//     → previous was cheaper, current is worse → true → BFF
//
//   current=80€, previous=100€ → sortByPrice → [current(80€), previous(100€)]
//     → current is already cheaper → false → no BFF
//
//   current=0.4, previous=0.8 → sortByRelevance → [previous(0.8), current(0.4)]
//     → previous had better relevance, current is worse → true → BFF
//
//   current=0.9, previous=0.8 → sortByRelevance → [current(0.9), previous(0.8)]
//     → current has better relevance → false → no BFF
//
//   current=undefined, previous=100€ → filtered dropped to zero while reference had results → true → BFF
//   current=undefined, previous=undefined → BFF also returned nothing (snapshotSortOptions saved all-undefined)
//     → !previous → false → no BFF — infinite loop naturally prevented
function isSortOptionWorse(
  sortFunction: (items: SearchResult[]) => SearchResult[],
  current?: SortOption,
  previous?: SortOption,
): boolean {
  if (!previous) return false;
  if (!current) return true;
  const items: [SearchResult, SearchResult] = [toFakeSearchResult(current), toFakeSearchResult(previous)];
  return sortFunction(items)[0] === items[1];
}

function resultBadges(result: SearchResult, reference: SortOptions, fastestMinutes?: number): Badges {
  return {
    bestOffer: result.relevanceScore !== undefined && result.relevanceScore === reference.bestOffer?.relevanceScore,
    cheapest: result.price?.total !== undefined && result.price.total === reference.cheapest?.price?.total,
    fastest: fastestMinutes !== undefined && durationToMinutes(result.travelDuration) === fastestMinutes,
    carbonEmission: !!result.carbonEmission && result.carbonEmission === reference.carbonEmission?.carbonEmission,
  };
}

function durationToMinutes(iso?: string): number | undefined {
  return iso === undefined ? undefined : Duration.fromISO(iso).as('minutes');
}
